import Sidebar from '@components/Layout/Sidebar/Sidebar';
import { useGetSidebarMenuItems } from '@components/Layout/Sidebar/useGetSidebarMenuItems';
import { GTranslate } from '@mui/icons-material';
import { Box, Container, IconButton } from '@mui/material';
import {
  authContext,
  EmployerSignUpModal,
  SignInModal,
  useGetProfile,
} from '@staffedupusa/shared-components';
import { lightGrey } from '@styles/colors';
import { trackViewedScreen } from '@utils/amplitudeTrackViewdScreen';
import { selectedPlanName } from '@utils/pricing';
import { useContext, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Outlet, useLocation } from 'react-router-dom';

import 'cropperjs/dist/cropper.css';
import CompanyExpiryModal from './CompanyExpiryModal/CompanyExpiryModal';
import DisabledCompanyModal from './DisabledCompanyModal/DisabledCompanyModal';
import FlagCompanyModal from './FlagCompanyModal/FlagCompanyModal';
import RemoveAccessModal from './RemoveAccessModal/RemoveAccessModal';

import { init } from '@amplitude/analytics-browser';
import './google-translation.scss';

// Extend Window type to include googleTranslateElementInit
declare global {
  interface Window {
    googleTranslateElementInit?: () => void;
    google?: any;
  }
}

const App = () => {
  const { authData } = useContext(authContext);
  const location = useLocation();
  const isCurrentPage = location.pathname;
  const [isExpiryModalOpen, setIsExpiryModalOpen] = useState(false);
  const [isCompanyFlagModal, setIsCompanyFlagModal] = useState(false);
  const [isCompanyDisabledModal, setIsCompanyDisabledModal] = useState(false);
  const [removeAccessOnModal, setRemoveAccessOnModal] = useState(false);
  const [titles, setTitles] = useState(['', '']);
  const userID = authData?.user?.id;
  const userCreatedAt = new Date(authData?.user?.dt_timestamp || Date.now());
  const thresholdDate = new Date('March 03, 2025 00:00 AM UTC');
  const titlesRef = useRef(titles);
  const { title } = document;
  const user = authData?.user;
  const company = user?.company;
  const enterprise = user?.enterprise;
  const isAdmin = authData?.user?.is_admin;
  const isApplicantOnly = !company && !enterprise && !isAdmin;
  // Ref for dropdown and icon button
  const dropdownRef = useRef<HTMLDivElement>(null);
  const iconButtonRef = useRef<HTMLButtonElement>(null);
  const [showTranslate, setShowTranslate] = useState(false);
  const [isGoogleTranslateLoaded, setIsGoogleTranslateLoaded] = useState(false);

  const titleMap: Record<string, string> = {
    'Dashboard | StaffedUp': 'Applicants',
    'Position Management | StaffedUp': 'Job_Management',
    'Digital Onboarding | StaffedUp': 'Onboarding',
  };

  const screenPrefix = titleMap[title] || title;
  const userPlan = selectedPlanName(company?.plan ?? 0);

  useGetProfile();
  const sidebarMenuItems = useGetSidebarMenuItems(
    company
      ? 'employer'
      : enterprise
        ? 'enterprise'
        : authData.user?.is_rockerbox
          ? 'rockerbox'
          : authData.user?.is_admin
            ? 'admin'
            : 'applicant',
  );

  // LEAD_CONNECTOR_CHAT_WIDGET
  useEffect(() => {
    if (company || enterprise) {
      const script = document.createElement('script');
      script.src = 'https://widgets.leadconnectorhq.com/loader.js';
      script.setAttribute(
        'data-resources-url',
        'https://widgets.leadconnectorhq.com/chat-widget/loader.js',
      );
      script.setAttribute(
        'data-widget-id',
        import.meta.env.VITE_LEAD_CONNECTOR_ID,
      );
      script.async = true;
      document.body.appendChild(script);

      return () => {
        document.body.removeChild(script);
      };
    }
  }, [authData]);

  useEffect(() => {
    if (
      company &&
      company?.plan === 0 &&
      isCurrentPage !== '/employer/account'
    ) {
      setIsExpiryModalOpen(true);
    } else if (company && company?.is_suspicious && company?.plan !== 0) {
      setIsCompanyFlagModal(true);
    } else if ((company || enterprise) && !user?.enabled) {
      setIsCompanyDisabledModal(true);
    } else if (
      company &&
      company.plan === 999 &&
      !user?.walkthrough_completed &&
      isCurrentPage !== '/dashboard' &&
      isCurrentPage !== '/dashboard/' &&
      userCreatedAt > thresholdDate
    ) {
      setRemoveAccessOnModal(true);
    }
  }, [authData, isCurrentPage]);

  // AMPLITUDE INITILIZE FOR COMPANY AND ENTERPRISE USERS
  useEffect(() => {
    if (company || enterprise) {
      init(import.meta.env.VITE_AMPLITUDE_API_KEY, {
        defaultTracking: {
          sessions: false,
          pageViews: false,
        },
      });
    }
  }, [userID]);
  useEffect(() => {
    titlesRef.current = titles; // Keep ref updated with the latest titles
  }, [titles]);

  useEffect(() => {
    setTitles(([_, curr]) => [curr, title]); // Update titles
    if (company || enterprise) {
      const previousTitle =
        titlesRef.current[0] !== title
          ? titlesRef.current[0]
          : document.referrer;
      trackViewedScreen(
        `${screenPrefix}_${userPlan}`,
        String(user?.id),
        previousTitle,
      );
    }
  }, [userID, document.title]);

  // Place click-outside useEffect after state and ref declarations
  useEffect(() => {
    if (!showTranslate) return;
    const handleClickOutside = (event: MouseEvent) => {
      const dropdown = dropdownRef.current;
      const iconBtn = iconButtonRef.current;
      if (
        dropdown &&
        !dropdown.contains(event.target as Node) &&
        iconBtn &&
        !iconBtn.contains(event.target as Node)
      ) {
        setShowTranslate(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showTranslate]);

  // Mount the script when isApplicantOnly is true
  useEffect(() => {
    if (!isApplicantOnly) return;

    if (!window.googleTranslateElementInit) {
      window.googleTranslateElementInit =
        function googleTranslateElementInit() {
          setIsGoogleTranslateLoaded(true);
        };
    }

    if (!document.getElementById('google-translate-script')) {
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.async = true;
      script.src =
        'https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
      script.id = 'google-translate-script';
      document.body.appendChild(script);
    }
  }, [isApplicantOnly]);

  // Cleanup when isApplicantOnly becomes false
  useEffect(() => {
    if (isApplicantOnly) return;

    // Remove the script
    const script = document.getElementById('google-translate-script');
    if (script) {
      script.remove();
    }

    // Clear widget content
    const el = document.getElementById('google_translate_element');
    if (el) {
      el.innerHTML = '';
    }

    // Remove global callback
    delete window.googleTranslateElementInit;

    // Reset state
    setIsGoogleTranslateLoaded(false);
    setShowTranslate(false);
  }, [isApplicantOnly]);

  // Initialize the widget after script is loaded
  useEffect(() => {
    if (!isApplicantOnly || !isGoogleTranslateLoaded) return;

    const el = document.getElementById('google_translate_element');
    if (el) {
      el.innerHTML = '';
    }

    new window.google.translate.TranslateElement(
      {
        pageLanguage: 'en',
        includedLanguages: 'en,es',
        layout: window.google.translate.TranslateElement.InlineLayout.SIMPLE,
      },
      'google_translate_element',
    );
  }, [isGoogleTranslateLoaded, isApplicantOnly]);

  return (
    <>
      <Helmet>
        <title>Dashboard | StaffedUp</title>
        <style>{`
          body {
            top: 0 !important;
          }
          body > .skiptranslate:not(iframe) {
            display: none !important;
          }
        `}</style>
      </Helmet>
      <Box
        bgcolor={lightGrey}
        display="flex"
        overflow="hidden"
        minHeight="100vh"
      >
        {authData.isAuth && authData.user && (
          <Sidebar menuItems={authData.user ? sidebarMenuItems : null} />
        )}
        <Container maxWidth={false} disableGutters>
          <Outlet />
        </Container>
      </Box>
      <EmployerSignUpModal />
      {isExpiryModalOpen && (
        <CompanyExpiryModal
          isExpiryModalOpen={isExpiryModalOpen}
          handleClose={() => {
            setIsExpiryModalOpen(false);
          }}
        />
      )}
      {isCompanyFlagModal && (
        <FlagCompanyModal
          isCompanyFlagModal={isCompanyFlagModal}
          handleClose={() => {
            setIsCompanyFlagModal(false);
          }}
        />
      )}
      {isCompanyDisabledModal && (
        <DisabledCompanyModal
          isCompanyDisabledModal={isCompanyDisabledModal}
          handleClose={() => {
            setIsCompanyDisabledModal(false);
          }}
        />
      )}
      {removeAccessOnModal && (
        <RemoveAccessModal
          removeAccessOnModal={removeAccessOnModal}
          handleClose={() => {
            setRemoveAccessOnModal(false);
          }}
        />
      )}
      <SignInModal />

      {/* Fixed top-right language button */}
      {isApplicantOnly && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 15,
            right: 15,
            zIndex: 999,
            textAlign: 'right',
          }}
        >
          <Box
            className="transl-dropdown"
            sx={{
              mb: showTranslate ? 2 : 0,
              bgcolor: 'white',
              borderRadius: 8,
              boxShadow: 3,
              transition: 'all 0.35s ease',
              p: 1,
              '& .skiptranslate.goog-te-gadget': {
                '& .goog-te-gadget-simple': {
                  border: '0 !important',
                },
              },
              opacity: showTranslate ? '1' : '0',
            }}
            ref={dropdownRef}
          >
            <div id="google_translate_element" />
          </Box>
          <IconButton
            sx={{
              backgroundColor: '#008fcc !important',
              color: '#fff !important',
              transition: 'background-color 0.35s ease',
              boxShadow:
                '0px 2px 4px -1px rgba(0,0,0,0.2),0px 4px 5px 0px rgba(0,0,0,0.14),0px 1px 10px 0px rgba(0,0,0,0.12)',
              '&:hover': { backgroundColor: '#067fb3ff !important' },
            }}
            size="large"
            onClick={() => setShowTranslate((v) => !v)}
            ref={iconButtonRef}
          >
            <GTranslate />
          </IconButton>
        </Box>
      )}
    </>
  );
};

export default App;
